import type { IGenericInputFactory, IImage, IMoleculeAlert } from '.';

export enum PAYMENT_TYPES {
  CREDIT_CARD = 'CreditCard',
  PAYPAL = 'PayPalAccount',
  NOT_AVAILABLE = 'NO_PAYMENT_METHOD'
}

export enum CREDIT_CARD_TYPES {
  MASTERCARD = 'MasterCard',
  VISA = 'Visa'
}

export interface ICreditCard {
  type: PAYMENT_TYPES.CREDIT_CARD;
  details: {
    cardType: CREDIT_CARD_TYPES;
    lastFour: string;
    expiry: string;
    cardExpiring?: boolean;
    cardExpired?: boolean;
  };
}

export interface IPayPal {
  type: PAYMENT_TYPES.PAYPAL;
  details: {
    email: string;
  };
}

export type IAnyPaymentMethod = ICreditCard | IPayPal;

export interface INoPaymentMethod {
  type: PAYMENT_TYPES.NOT_AVAILABLE;
}

export interface ICreditCardFields {
  inputCardNumber: IGenericInputFactory<'required' | 'invalid'>;
  inputExpiryDate: IGenericInputFactory<'required' | 'invalid'>;
  inputCVV: IGenericInputFactory<'required' | 'invalid'>;
}

export interface IPayPalFields {
  inputPayPal: IGenericInputFactory;
}

export interface IPayPalContent {
  agreementDescription?: string;
}

interface IBasePaymentMethod {
  heading: string;
  copy?: string;
  image?: IImage;
  successAlert?: IMoleculeAlert;
  errorAlert?: IMoleculeAlert;
  footerNote?: string;
}

export interface ICreditCardPaymentMethod extends IBasePaymentMethod {
  paymentType: PAYMENT_TYPES.CREDIT_CARD;
  inputs: ICreditCardFields;
}

export interface IPayPalPaymentMethod extends IBasePaymentMethod {
  paymentType: PAYMENT_TYPES.PAYPAL;
  inputs: IPayPalFields;
}

export type IPaymentMethod = ICreditCardPaymentMethod | IPayPalPaymentMethod;

export interface ITokenisedPaymentMethod {
  type: PAYMENT_TYPES;
  details: ICreditCard['details'] | IPayPal['details'];
  nonce: string;
  sessionId?: string;
}

export const isPayPal = (paymentMethod?: ICreditCard | IPayPal): paymentMethod is IPayPal =>
  paymentMethod?.type === PAYMENT_TYPES.PAYPAL;

export const isCreditCard = (paymentMethod?: IAnyPaymentMethod | INoPaymentMethod): paymentMethod is ICreditCard =>
  paymentMethod?.type === PAYMENT_TYPES.CREDIT_CARD;

export function isValidPaymentMethod(
  paymentMethod: IAnyPaymentMethod | undefined
): paymentMethod is IPayPal | ICreditCard {
  if (!paymentMethod?.type) {
    return false;
  }

  return !!paymentMethod.details;
}
